import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, getMoviesByCategory } from '@/mocks/movies';
import type { Movie, Category } from '@/mocks/movies';

function getCategoryBySlug(slug: string): Category | undefined {
  return categories.find(c => c.slug === slug);
}

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [displayMovies, setDisplayMovies] = useState<Movie[]>([]);

  const updateMovies = useCallback((categorySlug: string) => {
    setActiveSlug(categorySlug);
    setDisplayMovies(getMoviesByCategory(categorySlug));
  }, []);

  useEffect(() => {
    updateMovies(slug);
  }, [slug, updateMovies]);

  const currentCategory = getCategoryBySlug(activeSlug);
  const categoryName = currentCategory?.name || '全部';

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '电视剧大全', path: '/category/all' },
    ...(activeSlug !== 'all' ? [{ label: categoryName }] : []),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 木瓜影视高清电视剧全集免费在线观看`,
    description: `木瓜影视${categoryName}频道，提供${categoryName}高清全集免费在线观看，最新热播${categoryName}不缓冲无广告极速播放。`,
    url: `https://huarukai.cn/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '木瓜影视',
      url: 'https://huarukai.cn/',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: displayMovies.length,
      itemListElement: displayMovies.map((movie, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: movie.title,
          url: `https://huarukai.cn/detail/${movie.id}`,
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <main className="pt-16">
        <div className="bg-background-100/60">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {displayMovies.length} 部电视剧 — 高清全集免费在线观看，极速播放不卡顿
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
                <span className={`text-xs ${activeSlug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                  {cat.count}
                </span>
              </Link>
            ))}
          </div>

          {displayMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {displayMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-movie-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无电视剧资源</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 mt-3 text-sm text-primary-500 hover:text-primary-600 transition-colors"
              >
                返回全部电视剧
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}